/**
 * Cloudflare Turnstile loader and explicit-render helper.
 *
 * The widget is rendered explicitly rather than declaratively (`class="cf-turnstile"`) because the
 * newsletter modal stays mounted after a submit attempt: its error path returns the user to the same
 * live form. Turnstile tokens are single-use, so a second attempt needs `turnstile.reset(widgetId)`,
 * and that requires holding on to the widget id.
 *
 * The API script is fetched on demand — the first time a protected form is actually opened — so the
 * third-party request is not paid on every page view of the site.
 */

const API_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const SCRIPT_ID = 'cf-turnstile-api'

export interface TurnstileRenderOptions {
    sitekey: string
    action: string
    /** Called whenever the widget produces a fresh token. */
    onToken?: (token: string) => void
    /** Called when the challenge fails or the token expires; the held token is no longer usable. */
    onExpiredOrError?: () => void
}

interface TurnstileApi {
    render(container: HTMLElement, params: Record<string, unknown>): string
    reset(widgetId?: string): void
    remove(widgetId?: string): void
    getResponse(widgetId?: string): string | undefined
}

declare global {
    interface Window {
        turnstile?: TurnstileApi
    }
}

let loadPromise: Promise<void> | null = null

/** Loads the Turnstile API script once per page; concurrent callers share the same promise. */
export function loadTurnstile(): Promise<void> {
    if (window.turnstile) {
        return Promise.resolve()
    }

    if (loadPromise) {
        return loadPromise
    }

    loadPromise = new Promise<void>((resolve, reject) => {
        const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null

        if (existing) {
            existing.addEventListener('load', () => resolve())
            existing.addEventListener('error', () => reject(new Error('Turnstile script failed to load')))
            return
        }

        const script = document.createElement('script')
        script.id = SCRIPT_ID
        script.src = API_SCRIPT_URL
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = () => {
            // Allow a later attempt to retry rather than caching the failure forever. The element has
            // to go with the promise: left in place it would send the retry down the `existing` branch
            // above, which attaches listeners to a script that has already errored. Neither `load` nor
            // `error` fires a second time, so that promise would never settle and the caller would
            // await it forever.
            loadPromise = null
            script.remove()
            reject(new Error('Turnstile script failed to load'))
        }
        document.head.appendChild(script)
    })

    return loadPromise
}

/** Loads the API if needed and renders a widget into `container`, returning its widget id. */
export async function renderTurnstile(
    container: HTMLElement,
    options: TurnstileRenderOptions,
): Promise<string> {
    await loadTurnstile()

    if (!window.turnstile) {
        throw new Error('Turnstile API unavailable after load')
    }

    return window.turnstile.render(container, {
        sitekey: options.sitekey,
        action: options.action,
        callback: (token: string) => options.onToken?.(token),
        'expired-callback': () => options.onExpiredOrError?.(),
        'timeout-callback': () => options.onExpiredOrError?.(),
        'error-callback': () => options.onExpiredOrError?.(),
    })
}

/** Discards the spent token and issues a challenge for the next attempt. */
export function resetTurnstile(widgetId: string | null): void {
    if (widgetId && window.turnstile) {
        window.turnstile.reset(widgetId)
    }
}

export function removeTurnstile(widgetId: string | null): void {
    if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId)
    }
}
